/**
 * BlocosDeConfiguracao.js — os trechos da tela Configurações com desenho próprio
 * CI Sistemas de Informática Ltda. — AcessoiD (Onda 100)
 *
 * Os cartões do modo de filtragem, os sliders de limiar, a superexposição, o seletor
 * CPU/GPU, a barra de estado do motor e a linha do tempo do relógio. O CORPO de cada
 * um saiu de `pages/Settings.js` por recorte de linhas, sem redigitação: o que mudou
 * foi só o endereço. Cada bloco recebe por propriedade o que antes pegava do escopo
 * da página, com os mesmos nomes.
 */
import React from 'react';
import { SETTINGS_META } from '../../data/settingsMeta';

export function BlocoModoDeFiltragem({ settings, handleSave, saving }) {
  return (
              <div className="settings-field">
                <label className="field-label">Modo de Filtragem</label>
                <p className="field-desc">
                  Escolha a estratégia de filtragem. Os modos são mutuamente exclusivos.
                  Altere com cuidado em ambientes com frotas de placas sequenciais.
                </p>
                <div className="plate-filter-mode-cards">

                  {/* Modo: Consolidação por passagem (Onda 13) */}
                  <button
                    className={`plate-filter-card ${(settings.plate_filter_mode || 'none') === 'passage' ? 'active' : ''}`}
                    onClick={() => handleSave('plate_filter_mode', 'passage')}
                    disabled={saving}
                  >
                    <span className="pfc-icon">🗳️</span>
                    <span className="pfc-title">Consolidação por passagem (recomendado)</span>
                    <span className="pfc-desc">Junta as leituras da mesma passagem do veículo, vota caractere a caractere com a confiança real de cada um e registra uma única vez. Leitura forte registra na hora; leitura única e fraca é registrada ao fim da passagem, sem perder o veículo. Ignora placas paradas ao fundo.</span>
                    <span className="pfc-cost">Custo: leitura única e fraca só é registrada ao fim da passagem (até 3 s de atraso). Não perde veículos.</span>
                  </button>

                  {/* Modo: Nenhum */}
                  <button
                    className={`plate-filter-card ${(settings.plate_filter_mode || 'none') === 'none' ? 'active' : ''}`}
                    onClick={() => handleSave('plate_filter_mode', 'none')}
                    disabled={saving}
                  >
                    <span className="pfc-icon">🔓</span>
                    <span className="pfc-title">Nenhum</span>
                    <span className="pfc-desc">Todas as leituras válidas são registradas imediatamente, sem filtragem adicional.</span>
                    <span className="pfc-cost">Custo: uma leitura errada da mesma passagem vira um ticket fantasma ao lado do correto (só a deduplicação de placa idêntica segura repetições).</span>
                  </button>

                  {/* Modo: Similaridade */}
                  <button
                    className={`plate-filter-card ${(settings.plate_filter_mode || 'none') === 'similarity' ? 'active' : ''}`}
                    onClick={() => handleSave('plate_filter_mode', 'similarity')}
                    disabled={saving}
                  >
                    <span className="pfc-icon">🔤</span>
                    <span className="pfc-title">Similaridade</span>
                    <span className="pfc-desc">Descarta leituras de placas com até 1 caractere diferente de outra já registrada na janela de tempo.</span>
                    <span className="pfc-cost">Custo: a PRIMEIRA leitura vence. Se ela estiver errada, as leituras corretas seguintes são descartadas e o ticket fica com a placa errada (caso DSM8H77 em 05/09). Modo legado; prefira a consolidação por passagem.</span>
                  </button>

                  {/* Modo: Dupla Leitura */}
                  <button
                    className={`plate-filter-card ${(settings.plate_filter_mode || 'none') === 'double_read' ? 'active' : ''}`}
                    onClick={() => handleSave('plate_filter_mode', 'double_read')}
                    disabled={saving}
                  >
                    <span className="pfc-icon">✅</span>
                    <span className="pfc-title">Dupla Leitura</span>
                    <span className="pfc-desc">Registra somente se a mesma placa exata for lida 2 vezes dentro da janela de tempo.</span>
                    <span className="pfc-cost">Custo: veículo com uma única leitura válida na passagem (carro rápido, placa na borda, noite) NÃO é registrado; exige duas leituras idênticas e adiciona um ciclo de latência.</span>
                  </button>

                </div>
              </div>
  );
}

export function BarraDoMotor({ alprStatus }) {
  return (
    <>
          {alprStatus && (
            <div className="alpr-status-bar">
              <div className="alpr-status-item">
                <span className={`alpr-status-dot ${alprStatus.ready ? 'green' : 'red'}`} />
                <span className="alpr-status-label">Motor ALPR</span>
                <span className="alpr-status-value">{alprStatus.ready ? 'Online' : 'Offline'}</span>
              </div>
              <div className="alpr-status-item">
                <span className="alpr-status-label">Período atual</span>
                <span className={`alpr-period-badge period-${alprStatus.current_period || 'diurno'}`}>
                  {alprStatus.current_period === 'noturno' ? '🌙 Noturno'
                    : alprStatus.current_period === 'crepusculo' ? '🌅 Crepúsculo'
                    : '☀️ Diurno'}
                </span>
              </div>
              <div className="alpr-status-item">
                <span className="alpr-status-label">Threshold ativo</span>
                <span className="alpr-status-value alpr-threshold-value">{alprStatus.current_threshold ?? '—'}</span>
              </div>
              <div className="alpr-status-item">
                <span className="alpr-status-label">Uptime</span>
                <span className="alpr-status-value">
                  {alprStatus.uptime_seconds != null
                    ? alprStatus.uptime_seconds >= 3600
                      ? `${Math.floor(alprStatus.uptime_seconds / 3600)}h ${Math.floor((alprStatus.uptime_seconds % 3600) / 60)}min`
                      : `${Math.floor(alprStatus.uptime_seconds / 60)}min`
                    : '—'}
                </span>
              </div>
            </div>
          )}
    </>
  );
}

export function BlocoLimiares({ settings, setSettings, handleSave, saving, engineConfig }) {
  return (
            <div className="alpr-card">
              <div className="alpr-card-header">
                <div className="alpr-card-title-row">
                  <span className="alpr-card-icon">🌙</span>
                  <div>
                    <h3 className="alpr-card-title">Threshold Dinâmico Noturno</h3>
                    <p className="alpr-card-subtitle">Ajuste automático da confiança mínima por horário</p>
                  </div>
                </div>
                {/* Toggle principal */}
                <div className="alpr-toggle-row">
                  <span className="alpr-toggle-desc">
                    Quando habilitado, o sistema usa um limiar de confiança diferente para cada período do dia,
                    baseado em análise de 4.841 snapshots históricos de produção.
                  </span>
                  <button
                    className={`toggle-switch ${(settings.dynamic_threshold_enabled === 'true' || settings.dynamic_threshold_enabled === true) ? 'on' : 'off'}`}
                    onClick={() => handleSave('dynamic_threshold_enabled', !(settings.dynamic_threshold_enabled === 'true' || settings.dynamic_threshold_enabled === true))}
                    disabled={saving}
                  >
                    <span className="toggle-knob" />
                    <span className="toggle-text">{(settings.dynamic_threshold_enabled === 'true' || settings.dynamic_threshold_enabled === true) ? 'ATIVADO' : 'DESATIVADO'}</span>
                  </button>
                </div>
              </div>

              {/* Onda 26 (F-22): o que o motor está usando de fato agora */}
              {engineConfig ? (
                <div className="alpr-engine-now">
                  <strong>No motor agora:</strong>{' '}
                  período <b>{engineConfig.current_period || '—'}</b> por {engineConfig.last_period_source || engineConfig.threshold_mode || '—'}
                  {engineConfig.last_brightness_roi != null && <> (brilho medido {engineConfig.last_brightness_roi})</>}, limiar <b>{engineConfig.current_threshold}</b>.
                  {' '}Faixas de luz: noturno ≤ {engineConfig.luminance_night_max}, crepúsculo ≤ {engineConfig.luminance_dusk_max}, diurno acima.
                  {' '}Os valores abaixo são os gravados na plataforma; um valor diferente do efetivo indica que o motor ainda não foi reiniciado.
                </div>
              ) : (
                <div className="alpr-engine-now alpr-engine-now-off">Motor sem resposta: os valores abaixo são os gravados na plataforma e podem não ser os efetivos.</div>
              )}

              {/* Sliders de threshold */}
              <div className="alpr-thresholds">
                {[['confidence_threshold_day', '☀️ Diurno', engineConfig ? `luz > ${engineConfig.luminance_dusk_max}` : 'luz alta', '#f59e0b'],
                  ['confidence_threshold_dusk', '🌅 Crepúsculo', engineConfig ? `luz ${engineConfig.luminance_night_max + 1}–${engineConfig.luminance_dusk_max}` : 'luz média', '#f97316'],
                  ['confidence_threshold_night', '🌙 Noturno', engineConfig ? `luz ≤ ${engineConfig.luminance_night_max}` : 'luz baixa', '#6366f1']
                ].map(([key, label, hours, color]) => {
                  const val = parseFloat(settings[key] || SETTINGS_META[key]?.recommended || 0.6);
                  const pct = Math.round(val * 100);
                  const effective = engineConfig ? engineConfig[key] : null;
                  const differs = effective != null && Math.abs(Number(effective) - val) > 0.004;
                  return (
                    <div key={key} className="alpr-threshold-row">
                      <div className="alpr-threshold-header">
                        <span className="alpr-threshold-label">{label}</span>
                        <span className="alpr-threshold-hours">{hours}</span>
                        <span className="alpr-threshold-val" style={{color}}>{val.toFixed(2)}{effective != null && <span className={`alpr-threshold-effective ${differs ? 'differs' : ''}`} title="Valor efetivo no motor"> · motor {Number(effective).toFixed(2)}</span>}</span>
                      </div>
                      <div className="alpr-slider-wrap">
                        <input
                          type="range"
                          className="alpr-slider"
                          min="30" max="95" step="1"
                          value={pct}
                          style={{'--slider-color': color, '--slider-pct': `${((pct-30)/(95-30))*100}%`}}
                          onChange={e => setSettings(prev => ({ ...prev, [key]: (parseInt(e.target.value)/100).toFixed(2) }))}
                          onMouseUp={e => handleSave(key, (parseInt(e.target.value)/100).toFixed(2))}
                          onTouchEnd={e => handleSave(key, (parseInt(e.target.value)/100).toFixed(2))}
                          // Onda 101: pelo teclado (setas) o valor mudava na tela e nunca era
                          // gravado — só o mouse e o toque chamavam handleSave.
                          onKeyUp={e => handleSave(key, (parseInt(e.target.value)/100).toFixed(2))}
                          onBlur={e => handleSave(key, (parseInt(e.target.value)/100).toFixed(2))}
                          aria-label={`Confiança mínima — ${label}`}
                        />
                        <div className="alpr-slider-ticks">
                          <span>0,30</span><span>0,50</span><span>0,70</span><span>0,95</span>
                        </div>
                      </div>
                      <p className="alpr-threshold-hint">
                        {key === 'confidence_threshold_day' && 'Aplicado quando o brilho medido na zona está na faixa diurna (câmera 3: 112 a 130 de dia). Limiar mais alto descarta mais ruídos.'}
                        {key === 'confidence_threshold_dusk' && 'Faixa intermediária: entardecer, amanhecer e chuva forte. A transição da manhã acontece entre 08 h e 09 h nesta câmera.'}
                        {key === 'confidence_threshold_night' && 'Faixa noturna (câmera 3: 67 a 81 à noite, com a iluminação do pátio). A consolidação por passagem compensa o limiar menor.'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
  );
}

export function BlocoLinhaDoTempo() {
  return (
              <div className="alpr-timeline">
                <div className="alpr-timeline-label">Faixas horárias</div>
                <div className="alpr-timeline-bar">
                  {/* Noturno: 00h–05h (5h = 20.8%) */}
                  <div className="alpr-timeline-segment night" style={{width:'20.8%'}} title="Noturno: 00h–05h">
                    <span className="alpr-timeline-seg-label">00h–05h</span>
                  </div>
                  {/* Crepúsculo manhã: 05h–06h (1h = 4.2%) */}
                  <div className="alpr-timeline-segment dusk" style={{width:'4.2%'}} title="Crepúsculo: 05h–06h">
                  </div>
                  {/* Diurno: 06h–18h (12h = 50%) */}
                  <div className="alpr-timeline-segment day" style={{width:'50%'}} title="Diurno: 06h–18h">
                    <span className="alpr-timeline-seg-label">06h–18h</span>
                  </div>
                  {/* Crepúsculo tarde: 18h–22h (4h = 16.7%) */}
                  <div className="alpr-timeline-segment dusk" style={{width:'16.7%'}} title="Crepúsculo: 18h–22h">
                    <span className="alpr-timeline-seg-label">18h–22h</span>
                  </div>
                  {/* Noturno: 22h–00h (2h = 8.3%) */}
                  <div className="alpr-timeline-segment night" style={{width:'8.3%'}} title="Noturno: 22h–00h">
                    <span className="alpr-timeline-seg-label">22h–00h</span>
                  </div>
                </div>
                <div className="alpr-timeline-legend">
                  <span className="alpr-legend-item"><span className="alpr-legend-dot day" />☀️ Diurno</span>
                  <span className="alpr-legend-item"><span className="alpr-legend-dot dusk" />🌅 Crepúsculo</span>
                  <span className="alpr-legend-item"><span className="alpr-legend-dot night" />🌙 Noturno</span>
                </div>
              </div>
  );
}

export function BlocoSuperexposicao({ settings }) {
  return (
            <div className="alpr-card field-disabled" style={{position:'relative'}}>
              <div className="field-disabled-banner">
                ⚠️ Funcionalidade em manutenção — temporariamente indisponível
              </div>
              <div className="alpr-card-header">
                <div className="alpr-card-title-row">
                  <span className="alpr-card-icon">☀️</span>
                  <div>
                    <h3 className="alpr-card-title">Correção de Superexposição</h3>
                    <p className="alpr-card-subtitle">Recuperação de detalhes em placas saturadas por farol</p>
                  </div>
                </div>
                {/* Toggle principal */}
                <div className="alpr-toggle-row">
                  <span className="alpr-toggle-desc">
                    Detecta quando a região da placa está saturada por farol direto ou reflexo de sol
                    e aplica correção gamma para recuperar os detalhes antes do reconhecimento.
                  </span>
                  <button
                    className="toggle-switch off"
                    disabled={true}
                  >
                    <span className="toggle-knob" />
                    <span className="toggle-text">{(settings.overexp_enabled === 'true' || settings.overexp_enabled === true) ? 'ATIVADO' : 'DESATIVADO'}</span>
                  </button>
                </div>
              </div>

              {/* Diagrama visual do efeito gamma */}
              <div className="alpr-gamma-diagram">
                <div className="alpr-gamma-label">Efeito do Gamma de Correção</div>
                <div className="alpr-gamma-scale">
                  {[0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9].map(g => {
                    const brightness = Math.round((g ** (1/2.2)) * 100);
                    const currentGamma = parseFloat(settings.overexp_gamma || 0.4);
                    const isActive = Math.abs(g - currentGamma) < 0.06;
                    return (
                      <div key={g} className={`alpr-gamma-swatch ${isActive ? 'active' : ''}`}
                        style={{background: `rgb(${brightness},${brightness},${brightness})`}}
                        title={`Gamma ${g.toFixed(1)}`}
                      >
                        {isActive && <span className="alpr-gamma-active-label">{g.toFixed(1)}</span>}
                      </div>
                    );
                  })}
                </div>
                <div className="alpr-gamma-scale-labels">
                  <span>Mais escuro (0,1)</span>
                  <span>Mais claro (0,9)</span>
                </div>
              </div>

              {/* Parâmetros numéricos */}
              <div className="alpr-overexp-params">
                <div className="alpr-param-row">
                  <div className="alpr-param-info">
                    <span className="alpr-param-label">Limiar de Superexposição</span>
                    <span className="alpr-param-desc">Brilho médio (0–255) da região da placa acima do qual a correção é acionada. Padrão: 200 (78% de saturação).</span>
                  </div>
                  <div className="alpr-param-input-wrap">
                    <input
                      type="number"
                      className="alpr-param-input"
                      value={settings.overexp_threshold || '200'}
                      min="150" max="250" step="5"
                      onChange={undefined}
                      onBlur={undefined}
                      disabled={true}
                    />
                    <span className="alpr-param-unit">/ 255</span>
                  </div>
                </div>

                <div className="alpr-param-row">
                  <div className="alpr-param-info">
                    <span className="alpr-param-label">Gamma de Correção</span>
                    <span className="alpr-param-desc">Fator de compressão dos highlights. Valores menores escurecem mais a imagem. Recomendado: 0,40 para faróis diretos.</span>
                  </div>
                  <div className="alpr-param-input-wrap">
                    <input
                      type="number"
                      className="alpr-param-input"
                      value={settings.overexp_gamma || '0.40'}
                      min="0.10" max="0.90" step="0.05"
                      onChange={undefined}
                      onBlur={undefined}
                      disabled={true}
                    />
                    <span className="alpr-param-unit">gamma</span>
                  </div>
                </div>
              </div>

              {/* Nota técnica */}
              <div className="alpr-tech-note">
                <span className="alpr-tech-note-icon">💡</span>
                <p>A correção é aplicada apenas quando o brilho médio da região inferior do frame (onde a placa está posicionada) ultrapassa o limiar configurado. O denoising é executado uma única vez após a correção, evitando processamento duplo.</p>
              </div>
            </div>
  );
}

export function BlocoProcessador({ alprStatus, deviceSwitching, handleSwitchDevice }) {
  return (
            <div className="alpr-card">
              <div className="alpr-card-header">
                <div className="alpr-card-title-row">
                  <span className="alpr-card-icon">⚡</span>
                  <div>
                    <h3 className="alpr-card-title">Device de Inferência</h3>
                    <p className="alpr-card-subtitle">Processador utilizado pelo motor de reconhecimento de placas</p>
                  </div>
                </div>
              </div>

              {/* Status atual do device + controle interativo */}
              <div className="inference-device-status">

                {/* Badge de status atual */}
                <div className="inference-device-badge-row">
                  <div className={`inference-device-badge ${alprStatus?.inference_device === 'gpu' ? 'gpu' : 'cpu'}`}>
                    <span className="inference-device-icon">
                      {deviceSwitching ? '⏳' : alprStatus?.inference_device === 'gpu' ? '🎮' : '🖥️'}
                    </span>
                    <div className="inference-device-info">
                      <span className="inference-device-name">
                        {deviceSwitching ? 'Reiniciando...' : alprStatus?.inference_device === 'gpu' ? 'GPU (CUDA)' : 'CPU'}
                      </span>
                      <span className="inference-device-desc">
                        {deviceSwitching
                          ? 'O motor de reconhecimento está reiniciando. Aguarde ~30 segundos.'
                          : alprStatus?.inference_device === 'gpu'
                            ? 'Aceleração por GPU habilitada — ideal para múltiplas câmeras'
                            : 'Processamento por CPU — adequado para até 2 câmeras'}
                      </span>
                    </div>
                    <span className={`inference-device-status-dot ${deviceSwitching ? 'switching' : alprStatus?.inference_device === 'gpu' ? 'active' : 'inactive'}`} />
                  </div>
                </div>

                {/* Indicador de disponibilidade CUDA */}
                <div className="inference-cuda-row">
                  <span className="inference-cuda-label">CUDA disponível no servidor:</span>
                  <span className={`inference-cuda-value ${alprStatus?.cuda_available ? 'yes' : 'no'}`}>
                    {alprStatus == null ? '—' : alprStatus.cuda_available ? '✓ Sim' : '✗ Não'}
                  </span>
                </div>

                {/* Botões de seleção CPU / GPU */}
                <div className="inference-device-controls">
                  <span className="inference-controls-label">Alterar device:</span>
                  <div className="inference-controls-buttons">
                    <button
                      className={`inference-btn inference-btn-cpu ${alprStatus?.inference_device !== 'gpu' && !deviceSwitching ? 'active' : ''}`}
                      onClick={() => handleSwitchDevice('cpu')}
                      disabled={deviceSwitching || alprStatus?.inference_device !== 'gpu'}
                      title="Usar CPU para inferência"
                    >
                      🖥️ CPU
                    </button>
                    <button
                      className={`inference-btn inference-btn-gpu ${alprStatus?.inference_device === 'gpu' && !deviceSwitching ? 'active' : ''}`}
                      onClick={() => handleSwitchDevice('gpu')}
                      disabled={deviceSwitching || alprStatus?.inference_device === 'gpu' || !alprStatus?.cuda_available}
                      title={!alprStatus?.cuda_available ? 'GPU não disponível neste servidor' : 'Usar GPU (CUDA) para inferência'}
                    >
                      🎮 GPU (CUDA)
                    </button>
                  </div>
                  {!alprStatus?.cuda_available && (
                    <p className="inference-no-cuda-note">GPU não detectada neste servidor. O botão GPU ficará disponível quando uma GPU NVIDIA com NVIDIA Container Toolkit estiver instalada.</p>
                  )}
                </div>

                {/* Nota informativa */}
                <div className="inference-guide-note">
                  <span>💡</span>
                  <p>A GPU é recomendada a partir de <strong>3 ou mais câmeras simultâneas</strong>. Para 1–2 câmeras, a CPU é suficiente. A troca reinicia o motor de reconhecimento por ~30 segundos.</p>
                </div>

              </div>
            </div>
  );
}
